import { Alert, Divider, Snackbar, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { styled, useTheme } from "@mui/material/styles";
import { compose } from "ramda";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";
import { AppRouters } from "../../config/AppRouter/AppRouters.menu";
import { useAlert } from "../../contexts/alert.context";
import { useAuthoize } from "../../contexts/authorize.context";
import { ColorModeContext } from "../../contexts/color.context";
// import { useScreen } from "../../contexts/screen.context";
import Authenticate from "../Authenticate/Authenticate";
import AppBar from "../Common/AppBar/AppBar";
import AppContainer from "../Common/AppContainer/AppContainer";
import AppLink from "../Common/AppLink/AppLink";
import AppMenu from "../Common/AppMenu/AppMenu";
import AppMenuHeader from "../Common/AppMenu/AppMenuHeader/AppMenuHeader";
import ActiveMenu from "../Common/AppMenu/AppMenuList/ActiveMenu/ActiveMenu";
import AppMenuList from "../Common/AppMenu/AppMenuList/AppMenuList";
import AppListItemButton from "../Common/AppMenu/AppMenuListItem/AppListItemButton";
import AppListItemText from "../Common/AppMenu/AppMenuListItem/AppListItemText";
import AppMenuListItem from "../Common/AppMenu/AppMenuListItem/AppMenuListItem";
import AppToolbar from "../Common/AppToolbar/AppToolbar";
import ProtectedComponent, { verifyAuthorize } from "../Common/ProtectedComponent/ProtectedComponent";
import withAuthorized from "../HoC/withAuthorized";

const OutletBodyBoxStyled = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    "& .MuiPaper-root": {
        // height: `calc(100vh - 96px)`,
    },
}));

export type TemplateOption = {
    open: boolean;
    loader: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
};

const Template = () => {
    const theme = useTheme();
    const location = useLocation();
    const { permissions, userProfile } = useAuthoize();
    // const { screen } = useScreen();
    const { alert, setAlert } = useAlert();
    const isSM = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

    // const [collapseUserManagementOpen, setCollapseUserManagementOpen] = React.useState(false);
    // const [collapseE2EFunctionOpen, setCollapseE2EFunctionOpen] = React.useState(false);

    const colorMode = React.useContext(ColorModeContext);
    useMemo(() => colorMode, [colorMode]);

    const [templateOption, setTemplateOption] = React.useState<TemplateOption>({
        open: isSM ? false : true,
        loader: useState<boolean>(false),
    });
    useEffect(() => {
        setTemplateOption((prev: TemplateOption) => ({ ...prev, open: !isSM }));
        // setCollapseUserManagementOpen(isSM);
        // setCollapseE2EFunctionOpen(isSM);
    }, [isSM]);

    // const transitionDurationMemo = useMemo(
    //     () => ({
    //         enter: theme.transitions.duration.enteringScreen,
    //         exit: theme.transitions.duration.leavingScreen,
    //     }),
    //     [theme]
    // );

    const handleDrawerChange = useCallback(() => setTemplateOption((p) => ({ ...p, open: !p.open })), []);

    // const handleUserManagementChange = useCallback(() => setCollapseUserManagementOpen((p) => !p), []);

    // const handleE2EFunctionChange = useCallback(() => setCollapseE2EFunctionOpen((p) => !p), []);

    // const filterAppRouterType =
    //     (t: NavigateType) =>
    //     ({ type }: AppRouter) =>
    //         type === "management";

    // const MenuList = useMemo(
    //     () => (
    //         <Fragment>
    //             <Zoom
    //                 in={templateOption.open && isSM}
    //                 timeout={transitionDurationMemo}
    //                 unmountOnExit
    //                 style={{ transitionDelay: `${templateOption.open && isSM ? transitionDurationMemo.exit : 0}ms` }}
    //             >
    //                 <Box
    //                     sx={{
    //                         position: "absolute",
    //                         bottom: "10%",
    //                         width: "100%",
    //                         display: "flex",
    //                         justifyContent: "center",
    //                     }}
    //                 >
    //                     <Fab
    //                         sx={{
    //                             position: "absolute",
    //                         }}
    //                         aria-label="close"
    //                         color="primary"
    //                         onClick={() => isSM && handleDrawerChange()}
    //                     >
    //                         <CloseIcon color="secondary" />
    //                     </Fab>
    //                 </Box>
    //             </Zoom>
    //         </Fragment>
    //     ),
    //     [
    //         collapseE2EFunctionOpen,
    //         collapseUserManagementOpen,
    //         handleE2EFunctionChange,
    //         handleUserManagementChange,
    //         templateOption.open,
    //         handleDrawerChange,
    //         isSM,
    //         transitionDurationMemo,
    //     ]
    // );

    const activeMenuIndexMemo = useMemo(
        () =>
            AppRouters.filter((route) => verifyAuthorize(route.permissions, permissions)).findIndex(
                (route) => route.to === location.pathname
            ),
        [location, permissions]
    );

    if (!permissions) return <Authenticate />;
    return (
        <Fragment>
            <Snackbar
                open={!!alert}
                autoHideDuration={2000}
                onClose={() => setAlert(undefined)}
                anchorOrigin={{ horizontal: "right", vertical: "top" }}
            >
                <Alert severity={alert?.status} sx={{ width: "100%" }} onClose={() => setAlert(undefined)}>
                    {alert?.message}
                </Alert>
            </Snackbar>
            <CssBaseline />
            <AppBar userProfile={userProfile!} />
            <AppMenu>
                {activeMenuIndexMemo !== -1 && <ActiveMenu activeIndex={activeMenuIndexMemo} />}
                <AppMenuHeader />
                <AppMenuList>
                    {AppRouters.map(({ to, label, testId, permissions }, index) => (
                        <ProtectedComponent permissions={permissions} key={index}>
                            <AppLink to={to} onClick={() => isSM && handleDrawerChange()}>
                                <AppMenuListItem disablePadding>
                                    <AppListItemButton
                                        data-testid={testId}
                                        id={to}
                                        className="menu-button-item"
                                        disableTouchRipple
                                        status={location.pathname === to ? "active" : undefined}
                                    >
                                        <AppListItemText primary={label} />
                                    </AppListItemButton>
                                </AppMenuListItem>
                            </AppLink>
                        </ProtectedComponent>
                    ))}
                </AppMenuList>
            </AppMenu>
            <AppToolbar />
            <AppContainer>
                <Divider />
                <OutletBodyBoxStyled component="main" my={2}>
                    {/**Display Component with Outlet */}
                    <Outlet context={templateOption} />
                </OutletBodyBoxStyled>
            </AppContainer>
        </Fragment>
    );
};

export const useTemplateOption = () => {
    return useOutletContext<TemplateOption>();
};

const TemplateWrapped = compose(withAuthorized);
export default TemplateWrapped(Template);
